import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { UserPreferences } from '@airpost/shared';
import { ApiError, get, post, put, setCsrfToken, type SessionInfo } from './api.js';
import { setLocale } from './i18n.js';

interface SessionState {
  session: SessionInfo | null;
  loading: boolean;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  can(permission: string): boolean;
  /** Save the user's own choices (which feeds the start screen shows, …); the session reflects them at once. */
  updatePreferences(patch: UserPreferences): Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const apply = (s: SessionInfo | null) => {
    setSession(s);
    setCsrfToken(s?.csrfToken ?? '');
    setLocale(s?.user.locale ?? 'el');
  };

  // The cookie may still be valid while the API restarts (a deployment): ask again a few times before
  // showing the login form, otherwise a login with the old cookie fails on the CSRF check.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const s = await get<SessionInfo>('/api/auth/me');
          if (!cancelled) apply(s);
          break;
        } catch (e) {
          const signedOut = e instanceof ApiError && e.status === 401;
          if (signedOut || attempt >= 5 || cancelled) {
            if (!cancelled) apply(null);
            break;
          }
          await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value: SessionState = {
    session,
    loading,
    async login(username, password) {
      apply(await post<SessionInfo>('/api/auth/login', { username, password }));
    },
    async logout() {
      await post('/api/auth/logout');
      apply(null);
    },
    can: (p) => session?.permissions.includes(p) ?? false,
    async updatePreferences(patch) {
      const r = await put<{ ok: true; preferences: UserPreferences }>('/api/auth/preferences', patch);
      setSession((s) => (s ? { ...s, user: { ...s.user, preferences: r.preferences } } : s));
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('SessionProvider missing');
  return v;
}
